import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, toastError, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

export type InteractionType = {
  id: string;
  code: string;
  label: string;
  description: string;
  order: number;
  supports_direction: boolean;
  supports_duration: boolean;
  system_default: boolean;
};

export type InteractionCreatePayload = {
  kind_code: string;
  contact_id?: string | null;
  opportunity_id?: string | null;
  organization_id?: string | null;
  subject?: string | null;
  notes?: string | null;
  occurred_at: string;
  direction?: "inbound" | "outbound" | null;
  duration_minutes?: number | null;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  scope:
    | { contact_id: string }
    | { opportunity_id: string }
    | { organization_id: string };
};

function makeIdempotencyKey(): string {
  const g: any = globalThis;
  if (g.crypto?.randomUUID) return g.crypto.randomUUID();
  return "ik-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function nowLocalDateTime(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    d.getFullYear() +
    "-" + pad(d.getMonth() + 1) +
    "-" + pad(d.getDate()) +
    "T" + pad(d.getHours()) +
    ":" + pad(d.getMinutes())
  );
}

export function InteractionForm({ open, onOpenChange, onSuccess, scope }: Props) {
  const auth: any = useAuth();
  // Slice 1B correction 2 — the in-memory attempt metadata MUST be
  // bound to the ACTIVE (tenant, support-target, session-user,
  // linked-record, action) tuple. When ANY of these change, discard
  // the key so it can never cross tenants, sessions, or records.
  // The key itself remains a bare opaque UUID on the wire.
  const activeTenant = auth?.user?.tenant_id ?? null;
  const supportTarget = auth?.supportSession?.tenant_id ?? null;
  const sessionUser = auth?.user?.id ?? null;
  const bindingRef = useRef<string>("");
  const scopeKey = JSON.stringify(scope);
  const binding = `create|${activeTenant}|${supportTarget}|${sessionUser}|${scopeKey}`;

  const [types, setTypes] = useState<InteractionType[] | null>(null);
  const [typesError, setTypesError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [idemKey, setIdemKey] = useState<string>("");

  const [kindCode, setKindCode] = useState("");
  const [subject, setSubject] = useState("");
  const [notes, setNotes] = useState("");
  const [occurredLocal, setOccurredLocal] = useState<string>(nowLocalDateTime());
  const [direction, setDirection] = useState<"" | "inbound" | "outbound">("");
  const [durationStr, setDurationStr] = useState<string>("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    // Any change in the (tenant, support-target, session-user,
    // scope) tuple invalidates the current key. This runs
    // independently of the open/close effect so a background context
    // change (e.g. logout in another tab, support-target swap) still
    // clears the key immediately.
    if (bindingRef.current && bindingRef.current !== binding) {
      setIdemKey("");
    }
    bindingRef.current = binding;
  }, [binding]);

  useEffect(() => {
    if (!open) {
      // Slice 1B correction 2 — clear the opaque key on:
      //   * dialog close / cancel;
      //   * linked-record change (scope prop change re-triggers this
      //     effect via the deps below);
      // Success + definitive failure paths also clear it via the
      // submit() finally block below.
      setIdemKey("");
      return;
    }
    setKindCode("");
    setSubject("");
    setNotes("");
    setOccurredLocal(nowLocalDateTime());
    setDirection("");
    setDurationStr("");
    setErrors({});
    setIdemKey(makeIdempotencyKey());
    setLoading(true);
    api
      .get<{ items: InteractionType[] }>("/tenant/interaction-types")
      .then((r) => setTypes(r.data.items))
      .catch((e) => {
        setTypes([]);
        setTypesError(formatApiError(e));
      })
      .finally(() => setLoading(false));
  // Re-run when the linked record (scope) or the identity/tenant
  // binding changes so any partially typed submission does NOT reuse
  // the previous target's key.
  }, [open, scopeKey, binding]);

  const selectedType = types?.find((t) => t.code === kindCode) || null;

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!kindCode) e.kind_code = "Please choose an Interaction type.";
    if (!subject.trim() && !notes.trim()) e.content = "Enter a subject or notes.";
    if (subject.length > 200) e.subject = "Subject must be 200 characters or fewer.";
    if (notes.length > 5000) e.notes = "Notes must be 5000 characters or fewer.";
    if (!occurredLocal) e.occurred_at = "Please provide when it occurred.";
    if (durationStr) {
      const n = Number(durationStr);
      if (!Number.isInteger(n) || n < 0 || n > 1440) e.duration = "0–1440 minutes.";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function submit() {
    if (submitting) return; // prevent double-submit
    if (!validate()) return;
    // Slice 1B correction 2 — if the binding drifted since the key
    // was minted (tenant / support-target / session / scope change),
    // refuse to send with the stale key.
    if (!idemKey || bindingRef.current !== binding) {
      setIdemKey(makeIdempotencyKey());
      return;
    }
    setSubmitting(true);
    try {
      const payload: InteractionCreatePayload = {
        kind_code: kindCode,
        subject: subject.trim() || null,
        notes: notes.trim() || null,
        occurred_at: new Date(occurredLocal).toISOString(),
        ...(scope as any),
      };
      if (selectedType?.supports_direction && direction) payload.direction = direction;
      if (selectedType?.supports_duration && durationStr)
        payload.duration_minutes = Number(durationStr);
      await api.post("/crm/interactions", payload, {
        headers: { "Idempotency-Key": idemKey },
      });
      // Success → clear the opaque key so a subsequent unrelated
      // action starts fresh (spec: "clear it on success").
      setIdemKey("");
      onSuccess();
      onOpenChange(false);
    } catch (e: any) {
      const status = e?.response?.status;
      // Non-retryable failures — clear the key. Everything else keeps
      // the same key so a retry hits the server's idempotency record.
      if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
        setIdemKey(makeIdempotencyKey());
      }
      toastError(e);
    } finally {
      setSubmitting(false);
    }
  }

  const noActiveTypes = !loading && types?.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto"
        data-testid="interaction-form-dialog"
      >
        <DialogHeader>
          <DialogTitle>Log Interaction</DialogTitle>
          <DialogDescription>
            Record a real call, email, meeting, voicemail, SMS, or note.
            Fields adapt to the selected Interaction type.
          </DialogDescription>
        </DialogHeader>
        {loading && (
          <p className="text-sm text-slate-500" data-testid="interaction-form-loading">
            Loading interaction types…
          </p>
        )}
        {typesError && (
          <p className="text-sm text-rose-700" data-testid="interaction-form-types-error">
            {typesError}
          </p>
        )}
        {noActiveTypes && (
          <p className="text-sm text-slate-600" data-testid="interaction-form-no-types">
            No active Interaction types are available for this tenant. Please
            contact your administrator.
          </p>
        )}
        {!loading && types && types.length > 0 && (
          <div className="space-y-4">
            <div>
              <Label htmlFor="int-kind" className="text-xs">Type</Label>
              <Select value={kindCode} onValueChange={setKindCode}>
                <SelectTrigger id="int-kind" data-testid="interaction-form-kind">
                  <SelectValue placeholder="Select an Interaction type" />
                </SelectTrigger>
                <SelectContent>
                  {types.map((t) => (
                    <SelectItem key={t.code} value={t.code} data-testid={`interaction-kind-${t.code}`}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.kind_code && (
                <p className="text-xs text-rose-700 mt-1" data-testid="interaction-form-error-kind">
                  {errors.kind_code}
                </p>
              )}
            </div>

            {selectedType?.supports_direction && (
              <div>
                <Label htmlFor="int-direction" className="text-xs">Direction</Label>
                <Select value={direction} onValueChange={(v) => setDirection(v as any)}>
                  <SelectTrigger id="int-direction" data-testid="interaction-form-direction">
                    <SelectValue placeholder="(Optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inbound" data-testid="interaction-direction-inbound">Inbound</SelectItem>
                    <SelectItem value="outbound" data-testid="interaction-direction-outbound">Outbound</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {selectedType?.supports_duration && (
              <div>
                <Label htmlFor="int-duration" className="text-xs">Duration (minutes)</Label>
                <Input
                  id="int-duration"
                  type="number"
                  min={0}
                  max={1440}
                  inputMode="numeric"
                  value={durationStr}
                  onChange={(e) => setDurationStr(e.target.value)}
                  data-testid="interaction-form-duration"
                />
                {errors.duration && (
                  <p className="text-xs text-rose-700 mt-1" data-testid="interaction-form-error-duration">
                    {errors.duration}
                  </p>
                )}
              </div>
            )}

            <div>
              <Label htmlFor="int-subject" className="text-xs">Subject</Label>
              <Input
                id="int-subject"
                maxLength={200}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Short subject (optional if notes provided)"
                data-testid="interaction-form-subject"
              />
              {errors.subject && (
                <p className="text-xs text-rose-700 mt-1">{errors.subject}</p>
              )}
            </div>

            <div>
              <Label htmlFor="int-notes" className="text-xs">Notes</Label>
              <Textarea
                id="int-notes"
                maxLength={5000}
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Details of the interaction (optional if subject provided)"
                data-testid="interaction-form-notes"
              />
              {errors.notes && (
                <p className="text-xs text-rose-700 mt-1">{errors.notes}</p>
              )}
              {errors.content && (
                <p className="text-xs text-rose-700 mt-1" data-testid="interaction-form-error-content">
                  {errors.content}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="int-when" className="text-xs">Occurred at</Label>
              <Input
                id="int-when"
                type="datetime-local"
                value={occurredLocal}
                onChange={(e) => setOccurredLocal(e.target.value)}
                data-testid="interaction-form-occurred-at"
              />
              {errors.occurred_at && (
                <p className="text-xs text-rose-700 mt-1">{errors.occurred_at}</p>
              )}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="interaction-form-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || loading || noActiveTypes}
            data-testid="interaction-form-submit"
          >
            {submitting ? "Saving…" : "Log Interaction"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
